import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calendar, 
  Book, 
  PartyPopper, 
  Star, 
  Repeat, 
  Search,
  Clock,
  Users,
  Sparkles,
} from "lucide-react";

interface StreamTemplate {
  id: string;
  name: string;
  description: string;
  category: 'worship' | 'study' | 'event' | 'special' | 'recurring';
  titleFormat: string;
  defaultBlurb: string;
  defaultTime: string;
  defaultDuration: number;
  isRecurring: boolean;
  recurrencePattern?: string;
  recurrenceDayOfWeek?: number;
  defaultPlatforms: string[];
  defaultTags: string;
  isSystem: boolean;
  usageCount: number;
}

interface TemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: StreamTemplate) => void;
}

const categoryIcons = {
  worship: <Calendar className="w-5 h-5" />,
  study: <Book className="w-5 h-5" />,
  event: <PartyPopper className="w-5 h-5" />,
  special: <Star className="w-5 h-5" />,
  recurring: <Repeat className="w-5 h-5" />,
};

const categoryColors = {
  worship: 'bg-blue-500/10 text-blue-700 border-blue-200',
  study: 'bg-green-500/10 text-green-700 border-green-200',
  event: 'bg-purple-500/10 text-purple-700 border-purple-200',
  special: 'bg-yellow-500/10 text-yellow-700 border-yellow-200',
  recurring: 'bg-orange-500/10 text-orange-700 border-orange-200',
};

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function TemplatePickerModal({ isOpen, onClose, onSelect }: TemplatePickerModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const { data: templates = [], isLoading } = useQuery<StreamTemplate[]>({
    queryKey: ['/api/stream-templates'],
    enabled: isOpen,
  });

  // Filter templates based on search and category
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group templates by category
  const groupedTemplates = filteredTemplates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, StreamTemplate[]>);

  const handleSelectTemplate = (template: StreamTemplate) => {
    onSelect(template);
    onClose();
  };

  const renderTemplateCard = (template: StreamTemplate) => (
    <div
      key={template.id}
      className="border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer group"
      onClick={() => handleSelectTemplate(template)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${categoryColors[template.category]}`}>
            {categoryIcons[template.category]}
          </div>
          <div>
            <h3 className="font-semibold group-hover:text-primary transition-colors">
              {template.name}
            </h3>
            {template.description && (
              <p className="text-sm text-muted-foreground">{template.description}</p>
            )}
          </div>
        </div>
        {template.isSystem && (
          <Badge variant="secondary" className="text-xs">
            <Sparkles className="w-3 h-3 mr-1" />
            System
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {template.isRecurring && (
          <Badge variant="outline" className="text-xs">
            <Repeat className="w-3 h-3 mr-1" />
            {template.recurrencePattern} on {daysOfWeek[template.recurrenceDayOfWeek || 0]}
          </Badge>
        )}
        {template.defaultTime && (
          <Badge variant="outline" className="text-xs">
            <Clock className="w-3 h-3 mr-1" />
            {template.defaultTime}
          </Badge>
        )}
        {template.defaultDuration && (
          <Badge variant="outline" className="text-xs">
            {template.defaultDuration} min
          </Badge>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {template.defaultPlatforms?.map((platform) => (
            <Badge key={platform} variant="secondary" className="text-xs">
              {platform}
            </Badge>
          ))}
        </div>
        {template.usageCount > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="w-3 h-3" />
            {template.usageCount} uses
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Choose a Stream Template</DialogTitle>
          <DialogDescription>
            Select a template to quickly create a stream with predefined settings
          </DialogDescription>
        </DialogHeader>

        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Category tabs */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="worship">Worship</TabsTrigger>
            <TabsTrigger value="study">Study</TabsTrigger>
            <TabsTrigger value="event">Event</TabsTrigger>
            <TabsTrigger value="special">Special</TabsTrigger>
            <TabsTrigger value="recurring">Recurring</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Loading templates...</div>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-muted-foreground mb-2">No templates found</p>
                  {searchTerm && (
                    <Button variant="link" onClick={() => setSearchTerm('')}>
                      Clear search
                    </Button>
                  )}
                </div>
              </div>
            ) : selectedCategory === 'all' ? (
              <div className="space-y-6">
                {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
                  <div key={category}>
                    <h3 className="font-semibold mb-3 capitalize flex items-center gap-2">
                      {categoryIcons[category as keyof typeof categoryIcons]}
                      {category}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {categoryTemplates.map(renderTemplateCard)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredTemplates.map(renderTemplateCard)}
              </div>
            )}
          </div>
        </Tabs>

        <div className="border-t pt-4 flex justify-between">
          <Button variant="link" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => onSelect(null as any)}>
            Start from Scratch
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
